import type { RiftConfig } from "../rift/types";

/**
 * Merges rift configs in order, with later configs taking precedence per field.
 * Used at render time for blocks (e.g. dataset config + block overrides).
 *
 * - `limit`: last defined wins
 * - `groups`, `aggregates`, `footers`: per-key merge (later overrides per key)
 * - `filters`: additive concat (all filters apply)
 * - `sorts`: last non-empty array wins (block sort replaces dataset's)
 * - `hiddenColumns`: union/dedup (hide from both dataset + block)
 */
export function mergeRiftConfigs(
  ...riftConfigs: (RiftConfig | null)[]
): RiftConfig {
  const defined = riftConfigs.filter(Boolean) as RiftConfig[];

  const limit =
    defined.reduceRight(
      (acc, cfg) =>
        acc === undefined && cfg.limit !== undefined ? cfg.limit : acc,
      undefined as number | null | undefined,
    ) ?? null;

  const groups = Object.assign({}, ...defined.map((cfg) => cfg.groups ?? {}));
  const aggregates = Object.assign(
    {},
    ...defined.map((cfg) => cfg.aggregates ?? {}),
  );
  const footers = Object.assign({}, ...defined.map((cfg) => cfg.footers ?? {}));

  const filters = defined.flatMap((cfg) => cfg.filters ?? []);

  const sorts = defined.reduceRight(
    (acc, cfg) =>
      acc.length === 0 && (cfg.sorts?.length ?? 0) > 0
        ? (cfg.sorts ?? [])
        : acc,
    [] as RiftConfig["sorts"],
  );

  const hiddenColumns = [
    ...new Set(defined.flatMap((cfg) => cfg.hiddenColumns ?? [])),
  ];

  return { limit, groups, aggregates, footers, filters, sorts, hiddenColumns };
}
